import { type DataObjectsManager, DataType as IufDataType, type IDataObject } from '@inpart-io/devtools-iap';
import {
    advancedSearchManager,
    agreementDetailProperties,
    agreementDetailsManager,
    agreementDocumentProperties,
    agreementDocumentsManager,
    agreementLinkProperties,
    agreementRelationshipManager,
    agreementsManager,
    allianceDefaultFolderTemplatesManager,
    allianceDetailProperties,
    allianceDetailsManager,
    alliancesManager,
    amendmentProperties,
    amendmentsManager,
    briefProperties,
    briefsManager,
    campaignProperties,
    campaignsManager,
    conferenceProperties,
    conferencesManager,
    contactTagsManager,
    DataType,
    evaluationCommentsManager,
    externalLinkProperties,
    groupedIndexManager,
    internalContactAgreementProperties,
    internalContactAgreementsManager,
    internalContactAllianceProperties,
    internalContactAlliancesManager,
    internalContactLeadsManager,
    internalContactProperties,
    internalContactsManager,
    leadCommentsManager,
    leadLinkedCompaniesManager,
    leadLinkedCompaniesProperties,
    leadLinksManager,
    leadProperties,
    leadsManager,
    leadTagProperties,
    leadTagsManager,
    meetingsManager,
    obligationInstanceProperties,
    obligationInstancesManager,
    obligationProperties,
    obligationsManager,
    privateCompaniesManager,
    privateCompanyLinkManager,
    privateCompanyLinkProperties,
    privateCompanyProperties,
    privateCompanyTagsManager,
    privateContactAgreementProperties,
    privateContactAgreementsManager,
    privateContactAllianceProperties,
    privateContactAlliancesManager,
    privateContactLeadProperties,
    privateContactLeadsManager,
    privateContactProperties,
    privateContactsManager,
    privateDocumentProperties,
    privateDocumentsManager,
    privateEmailsManager,
    privateFieldCustomValuesManager,
    privateFoldersManager,
    privateLeadBoardProperties,
    privateLeadBoardsManager,
    privatePublicationProperties,
    privatePublicationsManager,
    privateTasksManager,
    sharedFieldCustomListValueProperties,
    sharedFieldCustomValuesManager,
    triggeringEventsManager,
    triggeringEventsProperties,
    userProfileProperties,
    userProfilesManager
} from '@inpart-io/padme-iap';

import { i18nService } from '@/services/i18n-service';
import { AGREEMENT_DEFAULT_FOLDER_TEMPLATE_MODULE } from '@/store/agreement-default-folder-template-module';
import { AGREEMENT_DETAIL_MODULE } from '@/store/agreement-detail-module';
import { AGREEMENT_DOCUMENT_MODULE } from '@/store/agreement-document-module';
import { AGREEMENT_LINK_MODULE } from '@/store/agreement-link-module';
import { AGREEMENT_MODULE } from '@/store/agreement-module';
import { AGREEMENT_RELATIONSHIP_MODULE } from '@/store/agreement-relationship-module';
import { ALLIANCE_DEFAULT_FOLDER_TEMPLATE_MODULE } from '@/store/alliance-default-folder-template-module.ts';
import { ALLIANCE_DETAIL_MODULE } from '@/store/alliance-detail-module';
import { ALLIANCE_MODULE } from '@/store/alliance-module';
import { AMENDMENT_MODULE } from '@/store/amendment-module';
import { BRIEF_MODULE } from '@/store/brief-module';
import { CAMPAIGN_MODULE } from '@/store/campaign-module';
import { CONFERENCE_MODULE } from '@/store/conference-module';
import { EVALUATION_COMMENT_MODULE } from '@/store/evaluation-comment-module';
import { EVALUATION_MODULE } from '@/store/evaluation-module';
import { EXTERNAL_LINK_MODULE } from '@/store/external-links-module';
import { GROUPED_INDEX_MODULE } from '@/store/grouped-index-module';
import { INTERNAL_CONTACT_AGREEMENT_MODULE } from '@/store/internal-contact-agreement-module';
import { INTERNAL_CONTACT_ALLIANCE_MODULE } from '@/store/internal-contact-alliance-module';
import { INTERNAL_CONTACT_LEAD_MODULE } from '@/store/internal-contact-lead-module';
import { INTERNAL_CONTACT_MODULE } from '@/store/internal-contact-module';
import { LEAD_ASSET_PROFILE_MODULE } from '@/store/lead-asset-profile-module';
import { LEAD_COMMENT_MODULE } from '@/store/lead-comment-module';
import { LEAD_COMPANY_PROFILE_MODULE } from '@/store/lead-company-profile-module';
import { LEAD_CONSUMER_HEALTH_PROFILE_MODULE } from '@/store/lead-consumer-health-profile-module';
import { LEAD_DEFAULT_FOLDER_TEMPLATE_MODULE } from '@/store/lead-default-folder-template-module';
import { LEAD_LINK_MODULE } from '@/store/lead-link-module';
import { LEAD_LINKED_COMPANIES_MODULE } from '@/store/lead-linked-companies-module';
import { LEAD_MODULE } from '@/store/lead-module';
import { LEAD_TAG_MODULE } from '@/store/lead-tag-module';
import { LEAD_TECHNOLOGY_PROFILE_MODULE } from '@/store/lead-technology-profile-module';
import { MEETING_MODULE } from '@/store/meeting-module';
import { OBLIGATION_INSTANCE_MODULE } from '@/store/obligation-instance-module';
import { OBLIGATION_MODULE } from '@/store/obligation-module';
import { PRIVATE_COMPANY_DEFAULT_FOLDER_TEMPLATE_MODULE } from '@/store/private-company-default-folder-template-module';
import { PRIVATE_COMPANY_LINK_MODULE } from '@/store/private-company-links-module';
import { PRIVATE_COMPANY_MODULE } from '@/store/private-company-module';
import { PRIVATE_CONTACT_AGREEMENT_MODULE } from '@/store/private-contact-agreement-module';
import { PRIVATE_CONTACT_ALLIANCE_MODULE } from '@/store/private-contact-alliance-module';
import { PRIVATE_CONTACT_LEAD_MODULE } from '@/store/private-contact-lead-module';
import { PRIVATE_CONTACT_MODULE } from '@/store/private-contact-module';
import { PRIVATE_DOCUMENT_MODULE } from '@/store/private-document-module';
import { PRIVATE_EMAIL_MODULE } from '@/store/private-email-module';
import { PRIVATE_FOLDER_MODULE } from '@/store/private-folder-module';
import { PRIVATE_LEAD_BOARD_MODULE } from '@/store/private-lead-board-module';
import { PRIVATE_PUBLICATION_MODULE } from '@/store/private-publications-module';
import { PRIVATE_TASK_MODULE } from '@/store/private-task-module';
import { SHARED_FIELD_CUSTOM_VALUE_MODULE } from '@/store/shared-field-custom-value-module';
import { TRIGGERING_EVENTS_MODULE } from '@/store/triggering-events-module';
import { USER_PROFILE_MODULE } from '@/store/user-profile-module';

export function useDataObjectHelper(): {
    getDataObjectManager: (dataType: DataType | IufDataType | string) => DataObjectsManager<IDataObject>;
    getDataObjectModule: (dataType: DataType | IufDataType | string) => string;
    getDataObjectDefaultProperties: (dataType: DataType | IufDataType) => string;
} {
    const getDataObjectManager = (dataType: DataType | IufDataType | string): DataObjectsManager<IDataObject> => {
        switch (dataType) {
            case DataType.Agreement:
                return agreementsManager;
            case DataType.AgreementDetail:
                return agreementDetailsManager;
            case DataType.AgreementRelationship:
                return agreementRelationshipManager;
            case DataType.Alliance:
                return alliancesManager;
            case DataType.AllianceDetail:
                return allianceDetailsManager;
            case DataType.AllianceDefaultFolderTemplate:
                return allianceDefaultFolderTemplatesManager;
            case DataType.Amendment:
                return amendmentsManager;
            case DataType.Brief:
                return briefsManager;
            case DataType.EvaluationComment:
                return evaluationCommentsManager;
            case DataType.PrivateCompany:
                return privateCompaniesManager;
            case DataType.Lead:
                return leadsManager;
            case IufDataType.UserProfile:
                return userProfilesManager;
            case DataType.InternalContact:
                return internalContactsManager;
            case DataType.InternalContactAgreement:
                return internalContactAgreementsManager;
            case DataType.InternalContactAlliance:
                return internalContactAlliancesManager;
            case DataType.InternalContactLead:
                return internalContactLeadsManager;
            case DataType.LeadTag:
                return leadTagsManager;
            case DataType.Conference:
                return conferencesManager;
            case DataType.Campaign:
                return campaignsManager;
            case DataType.PrivateDocument:
                return privateDocumentsManager;
            case DataType.PrivateEmail:
                return privateEmailsManager;
            case DataType.PrivateFieldCustomValue:
                return privateFieldCustomValuesManager;
            case DataType.ShareFieldCustomValue:
                return sharedFieldCustomValuesManager;
            case DataType.PrivateCompanyLink:
                return privateCompanyLinkManager;
            case DataType.PrivateCompanyTag:
                return privateCompanyTagsManager;
            case DataType.PrivateLeadBoard:
                return privateLeadBoardsManager;
            case DataType.PrivateTask:
                return privateTasksManager;
            case DataType.LeadComment:
                return leadCommentsManager;
            case DataType.LeadLinkedCompany:
                return leadLinkedCompaniesManager;
            case DataType.LeadLink:
                return leadLinksManager;
            case DataType.Meeting:
                return meetingsManager;
            case DataType.GroupedDataObjectIdentity:
                return groupedIndexManager;
            case DataType.AdvancedSearchDataObjectIdentity:
                return advancedSearchManager;
            case DataType.AgreementDocument:
                return agreementDocumentsManager;
            case DataType.PrivateFolder:
                return privateFoldersManager;
            case DataType.PrivateContact:
                return privateContactsManager;
            case DataType.Obligation:
                return obligationsManager;
            case DataType.ObligationInstance:
                return obligationInstancesManager;
            case DataType.TriggeringEvent:
                return triggeringEventsManager;
            case DataType.ContactTag:
                return contactTagsManager;
            case DataType.PrivateContactAgreement:
                return privateContactAgreementsManager;
            case DataType.PrivateContactAlliance:
                return privateContactAlliancesManager;
            case DataType.PrivateContactLead:
                return privateContactLeadsManager;
            case DataType.PrivatePublication:
                return privatePublicationsManager;
            default: {
                const message = i18nService.t('DATAOBJECT_HELPER_ERROR.MANAGER', { dataType });
                console.error(message);
            }
        }
    };

    const getDataObjectModule = (dataType: DataType | IufDataType | string): string => {
        switch (dataType) {
            case DataType.Agreement:
                return AGREEMENT_MODULE;
            case DataType.AgreementRelationship:
                return AGREEMENT_RELATIONSHIP_MODULE;
            case DataType.Alliance:
                return ALLIANCE_MODULE;
            case DataType.AllianceDetail:
                return ALLIANCE_DETAIL_MODULE;
            case DataType.AllianceDefaultFolderTemplate:
                return ALLIANCE_DEFAULT_FOLDER_TEMPLATE_MODULE;
            case DataType.Amendment:
                return AMENDMENT_MODULE;
            case DataType.Brief:
                return BRIEF_MODULE;
            case DataType.PrivateCompany:
                return PRIVATE_COMPANY_MODULE;
            case DataType.Lead:
                return LEAD_MODULE;
            case DataType.Evaluation:
                return EVALUATION_MODULE;
            case DataType.EvaluationComment:
                return EVALUATION_COMMENT_MODULE;
            case IufDataType.UserProfile:
                return USER_PROFILE_MODULE;
            case DataType.LeadTag:
                return LEAD_TAG_MODULE;
            case DataType.LeadLinkedCompany:
                return LEAD_LINKED_COMPANIES_MODULE;
            case DataType.Conference:
                return CONFERENCE_MODULE;
            case DataType.Campaign:
                return CAMPAIGN_MODULE;
            case DataType.Meeting:
                return MEETING_MODULE;
            case DataType.InternalContactAgreement:
                return INTERNAL_CONTACT_AGREEMENT_MODULE;
            case DataType.InternalContactAlliance:
                return INTERNAL_CONTACT_ALLIANCE_MODULE;
            case DataType.InternalContactLead:
                return INTERNAL_CONTACT_LEAD_MODULE;
            case DataType.InternalContact:
                return INTERNAL_CONTACT_MODULE;
            case DataType.ShareFieldCustomValue:
                return SHARED_FIELD_CUSTOM_VALUE_MODULE;
            case DataType.PrivateCompanyLink:
                return PRIVATE_COMPANY_LINK_MODULE;
            case DataType.PrivateDocument:
                return PRIVATE_DOCUMENT_MODULE;
            case DataType.PrivateLeadBoard:
                return PRIVATE_LEAD_BOARD_MODULE;
            case DataType.ExternalLink:
                return EXTERNAL_LINK_MODULE;
            case DataType.AgreementDetail:
                return AGREEMENT_DETAIL_MODULE;
            case DataType.AgreementDocument:
                return AGREEMENT_DOCUMENT_MODULE;
            case DataType.AgreementLink:
                return AGREEMENT_LINK_MODULE;
            case DataType.LeadLink:
                return LEAD_LINK_MODULE;
            case DataType.GroupedDataObjectIdentity:
                return GROUPED_INDEX_MODULE;
            case DataType.AdvancedSearchDataObjectIdentity:
                return GROUPED_INDEX_MODULE;
            case DataType.PrivateEmail:
                return PRIVATE_EMAIL_MODULE;
            case DataType.PrivateFolder:
                return PRIVATE_FOLDER_MODULE;
            case DataType.LeadDefaultFolderTemplate:
                return LEAD_DEFAULT_FOLDER_TEMPLATE_MODULE;
            case DataType.PrivateCompanyDefaultFolderTemplate:
                return PRIVATE_COMPANY_DEFAULT_FOLDER_TEMPLATE_MODULE;
            case DataType.AgreementDefaultFolderTemplate:
                return AGREEMENT_DEFAULT_FOLDER_TEMPLATE_MODULE;
            case DataType.PrivateContact:
                return PRIVATE_CONTACT_MODULE;
            case DataType.LeadComment:
                return LEAD_COMMENT_MODULE;
            case DataType.LeadTechnologyProfile:
                return LEAD_TECHNOLOGY_PROFILE_MODULE;
            case DataType.LeadConsumerHealthProfile:
                return LEAD_CONSUMER_HEALTH_PROFILE_MODULE;
            case DataType.LeadAssetProfile:
                return LEAD_ASSET_PROFILE_MODULE;
            case DataType.LeadCompanyProfile:
                return LEAD_COMPANY_PROFILE_MODULE;
            case DataType.Obligation:
                return OBLIGATION_MODULE;
            case DataType.ObligationInstance:
                return OBLIGATION_INSTANCE_MODULE;
            case DataType.PrivateTask:
                return PRIVATE_TASK_MODULE;
            case DataType.TriggeringEvent:
                return TRIGGERING_EVENTS_MODULE;
            case DataType.PrivateContactLead:
                return PRIVATE_CONTACT_LEAD_MODULE;
            case DataType.PrivateContactAgreement:
                return PRIVATE_CONTACT_AGREEMENT_MODULE;
            case DataType.PrivateContactAlliance:
                return PRIVATE_CONTACT_ALLIANCE_MODULE;
            case DataType.PrivatePublication:
                return PRIVATE_PUBLICATION_MODULE;
            default: {
                const message = i18nService.t('DATAOBJECT_HELPER_ERROR.MANAGER', { dataType });
                console.error(message);
            }
        }
    };

    const getDataObjectDefaultProperties = (dataType: DataType | IufDataType): string => {
        switch (dataType) {
            case DataType.Brief:
                return briefProperties.DEFAULT;
            case DataType.PrivateCompany:
                return privateCompanyProperties.DEFAULT;
            case DataType.Lead:
                return leadProperties.DEFAULT;
            case DataType.LeadLinkedCompany:
                return leadLinkedCompaniesProperties.DEFAULT;
            case IufDataType.UserProfile:
                return userProfileProperties.DEFAULT;
            case DataType.LeadTag:
                return leadTagProperties.DEFAULT;
            case DataType.Conference:
                return conferenceProperties.DEFAULT;
            case DataType.Campaign:
                return campaignProperties.DEFAULT;
            case DataType.ShareFieldCustomValue:
                return sharedFieldCustomListValueProperties.DEFAULT;
            case DataType.PrivateCompanyLink:
                return privateCompanyLinkProperties.DEFAULT;
            case DataType.InternalContactLead:
                return internalContactProperties.DEFAULT;
            case DataType.InternalContactAgreement:
                return internalContactAgreementProperties.DEFAULT;
            case DataType.InternalContactAlliance:
                return internalContactAllianceProperties.DEFAULT;
            case DataType.InternalContact:
                return internalContactProperties.DEFAULT;
            case DataType.PrivateDocument:
                return privateDocumentProperties.DEFAULT;
            case DataType.ExternalLink:
                return externalLinkProperties.DEFAULT;
            case DataType.AgreementDetail:
                return agreementDetailProperties.DEFAULT;
            case DataType.AgreementDocument:
                return agreementDocumentProperties.DEFAULT;
            case DataType.AgreementLink:
                return agreementLinkProperties.DEFAULT;
            case DataType.AllianceDetail:
                return allianceDetailProperties.DEFAULT;
            case DataType.PrivateLeadBoard:
                return privateLeadBoardProperties.DEFAULT;
            case DataType.Amendment:
                return amendmentProperties.DEFAULT;
            case DataType.PrivateContact:
                return privateContactProperties.DEFAULT;
            case DataType.PrivateContactAgreement:
                return privateContactAgreementProperties.DEFAULT;
            case DataType.PrivateContactAlliance:
                return privateContactAllianceProperties.DEFAULT;
            case DataType.PrivateContactLead:
                return privateContactLeadProperties.DEFAULT;
            case DataType.Obligation:
                return obligationProperties.DEFAULT;
            case DataType.ObligationInstance:
                return obligationInstanceProperties.DEFAULT;
            case DataType.TriggeringEvent:
                return triggeringEventsProperties.DEFAULT;
            case DataType.PrivatePublication:
                return privatePublicationProperties.DEFAULT;
            default: {
                const message = i18nService.t('DATAOBJECT_HELPER_ERROR.MANAGER', { dataType });
                console.error(message);
            }
        }
    };

    return { getDataObjectManager, getDataObjectModule, getDataObjectDefaultProperties };
}
